import { createAsyncThunk } from '@reduxjs/toolkit';
import type { StepId } from '@store/user/interfaces/userInterfaces';

import type {
  FinanceStepData,
  GetUserByIdResponse,
  JobStepData,
  PersonalStepData,
  ReferencesStepData,
  ResidenceStepData,
  StepData,
  StepSubmissionResponse,
} from '../api/userApi';
import { UserApiService } from '../api/userApi';

// Base thunk config
interface ThunkApiConfig {
  rejectValue: {
    message: string;
    status?: number;
    code?: string;
  };
}

// Thunk to fetch user by ID
export const getUserByIdThunk = createAsyncThunk<
  GetUserByIdResponse,
  { userId: string },
  ThunkApiConfig
>('user/getUserById', async ({ userId }, { rejectWithValue }) => {
  try {
    const response = await UserApiService.getUserById(userId);

    if (!response.success || !response.data) {
      return rejectWithValue({
        message: response.error?.message || 'Failed to fetch user data',
        status: response.error?.status,
        code: response.error?.code,
      });
    }

    return response.data;
  } catch {
    return rejectWithValue({
      message: 'An unexpected error occurred while fetching user data',
      status: 0,
    });
  }
});

// Generic step submission thunk
export const submitStepThunk = createAsyncThunk<
  StepSubmissionResponse,
  { userId: string; step: StepId; data: StepData },
  ThunkApiConfig
>('user/submitStep', async ({ userId, step, data }, { rejectWithValue }) => {
  try {
    const response = await UserApiService.submitStep(userId, step, data);

    if (!response.success || !response.data) {
      return rejectWithValue({
        message: response.error?.message || 'Failed to submit step',
        status: response.error?.status,
        code: response.error?.code,
      });
    }

    return response.data;
  } catch {
    return rejectWithValue({
      message: 'An unexpected error occurred',
      status: 0,
    });
  }
});

// Personal step submission thunk
export const submitPersonalStepThunk = createAsyncThunk<
  StepSubmissionResponse,
  { userId: string; data: PersonalStepData },
  ThunkApiConfig
>('user/submitPersonalStep', async ({ userId, data }, { rejectWithValue }) => {
  try {
    const response = await UserApiService.submitPersonalStep(userId, data);

    if (!response.success || !response.data) {
      return rejectWithValue({
        message: response.error?.message || 'Failed to submit personal step',
        status: response.error?.status,
        code: response.error?.code,
      });
    }

    return response.data;
  } catch {
    return rejectWithValue({
      message: 'An unexpected error occurred while submitting personal data',
      status: 0,
    });
  }
});

// Residence step submission thunk
export const submitResidenceStepThunk = createAsyncThunk<
  StepSubmissionResponse,
  { userId: string; data: ResidenceStepData },
  ThunkApiConfig
>('user/submitResidenceStep', async ({ userId, data }, { rejectWithValue }) => {
  try {
    const response = await UserApiService.submitResidenceStep(userId, data);

    if (!response.success || !response.data) {
      return rejectWithValue({
        message: response.error?.message || 'Failed to submit residence step',
        status: response.error?.status,
        code: response.error?.code,
      });
    }

    return response.data;
  } catch {
    return rejectWithValue({
      message: 'An unexpected error occurred while submitting residence data',
      status: 0,
    });
  }
});

// Finance step submission thunk
export const submitFinanceStepThunk = createAsyncThunk<
  StepSubmissionResponse,
  { userId: string; data: FinanceStepData },
  ThunkApiConfig
>('user/submitFinanceStep', async ({ userId, data }, { rejectWithValue }) => {
  try {
    const response = await UserApiService.submitFinanceStep(userId, data);

    if (!response.success || !response.data) {
      return rejectWithValue({
        message: response.error?.message || 'Failed to submit finance step',
        status: response.error?.status,
        code: response.error?.code,
      });
    }

    return response.data;
  } catch {
    return rejectWithValue({
      message: 'An unexpected error occurred while submitting finance data',
      status: 0,
    });
  }
});

// Job step submission thunk
export const submitJobStepThunk = createAsyncThunk<
  StepSubmissionResponse,
  { userId: string; data: JobStepData },
  ThunkApiConfig
>('user/submitJobStep', async ({ userId, data }, { rejectWithValue }) => {
  try {
    const response = await UserApiService.submitJobStep(userId, data);

    if (!response.success || !response.data) {
      return rejectWithValue({
        message: response.error?.message || 'Failed to submit job step',
        status: response.error?.status,
        code: response.error?.code,
      });
    }

    return response.data;
  } catch {
    return rejectWithValue({
      message: 'An unexpected error occurred while submitting job data',
      status: 0,
    });
  }
});

// References step submission thunk
export const submitReferencesStepThunk = createAsyncThunk<
  StepSubmissionResponse,
  { userId: string; data: ReferencesStepData },
  ThunkApiConfig
>(
  'user/submitReferencesStep',
  async ({ userId, data }, { rejectWithValue }) => {
    try {
      // Send the transformed references data (filtered for empty values)
      const response = await UserApiService.submitReferencesStep(userId, data);

      if (!response.success || !response.data) {
        return rejectWithValue({
          message:
            response.error?.message || 'Failed to submit references step',
          status: response.error?.status,
          code: response.error?.code,
        });
      }

      return response.data;
    } catch {
      return rejectWithValue({
        message:
          'An unexpected error occurred while submitting references data',
        status: 0,
      });
    }
  },
);

// Validation step submission thunk (now fetches user data)
export const submitValidationStepThunk = createAsyncThunk<
  GetUserByIdResponse,
  { userId: string },
  ThunkApiConfig
>('user/submitValidationStep', async ({ userId }, { rejectWithValue }) => {
  try {
    const response = await UserApiService.submitValidationStep(userId);
    if (!response.success || !response.data) {
      return rejectWithValue({
        message:
          response.error?.message || 'Failed to fetch user data for validation',
        status: response.error?.status,
        code: response.error?.code,
      });
    }
    return response.data;
  } catch {
    return rejectWithValue({
      message: 'An unexpected error occurred while fetching user data',
      status: 0,
    });
  }
});
